export const EXPLORE_CATEGORIES = ["Openings", "Variations", "Puzzles", "Lessons", "Games", "Endgames", "Champions", "Glossary"] as const;
export type ExploreCategory = typeof EXPLORE_CATEGORIES[number];
export type ExploreItem = {
  href: string;
  title: string;
  description: string;
  category: ExploreCategory;
  meta: string;
  keywords: string;
  image?: string;
  preview?: boolean;
  visual: { fen?: string; piece?: "p" | "n" | "b" | "r" | "q" | "k"; orientation?: "white" | "black" };
};

export type ExploreDesign = "cabinet" | "atlas" | "journey";

export const EXPLORE_TRAILS = [
  { id: "first-plan", title: "Find your first plan", label: "Opening ideas", description: "Meet a classic opening, see development in action, then learn to finish.", pieces: ["p", "n", "r"], stops: ["/openings/italian-game", "/games/opera-game", "/endgames/queen-mate"] },
  { id: "bold-moves", title: "Think a little bolder", label: "Attacking chess", description: "Explore an unbalanced opening and two games where activity beats material.", pieces: ["n", "b", "q"], stops: ["/openings/sicilian-defense", "/games/immortal-game", "/games/evergreen-game"] },
  { id: "quiet-strength", title: "Make less mean more", label: "Endgame clarity", description: "A queen, a rook, a single pawn. Discover how small advantages become wins.", pieces: ["q", "r", "p"], stops: ["/endgames/queen-mate", "/endgames/rook-mate", "/endgames/king-and-pawn-opposition"] },
] as const;

export const normalizeDiscovery = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[’']/g, "").toLowerCase();

export function filterDiscoveries(items: ExploreItem[], query: string, category: ExploreCategory | "All", savedOnly: boolean, saved: readonly string[]) {
  const words = normalizeDiscovery(query).trim().split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    if (category !== "All" && item.category !== category) return false;
    if (savedOnly && !saved.includes(item.href)) return false;
    const text = normalizeDiscovery(`${item.title} ${item.description} ${item.category} ${item.meta} ${item.keywords}`);
    return words.every((word) => text.includes(word));
  });
}

export const EXPLORE_STORAGE_KEY = "ponderchess-explore-saved";
export const EMPTY_SAVED: string[] = [];

export function readSavedDiscoveries(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((item): item is string => typeof item === "string" && (
    /^\/(?:openings|games|endgames|lessons|champions)\/[a-z0-9-]+$/.test(item) ||
    /^\/openings\/[a-z0-9-]+\?line=\d+#opening-explorer$/.test(item) ||
    /^\/(?:champions|glossary)#[a-z0-9-]+$/.test(item) ||
    /^\/puzzles\?id=[a-zA-Z0-9]+$/.test(item) ||
    /^\/learn\/(?:rules|special-moves\/[a-z0-9-]+)$/.test(item) ||
    item === "/champions"
  )))].slice(0, 10000);
}

export const EXPLORE_PAGE_SIZE = 18;
export type ExplorePage = { items: ExploreItem[]; total: number; page: number; pages: number };
export type ExploreCounts = Record<ExploreCategory, number>;
export type ExploreQuery = { query?: string; category?: ExploreCategory | "All"; savedOnly?: boolean; saved?: string[]; sort?: "curated" | "title"; page?: number };

/** Invalid or stale deep links safely return to the family's main line. */
export function openingLineIndex(value: string | null, count: number): number {
  if (!value || !/^\d+$/.test(value)) return 0;
  const index = Number(value);
  return Number.isSafeInteger(index) && index < count ? index : 0;
}
